// Studio Mode shared types. Mirrors decoda/schemas/photo_studio.py.

export type StudioBackdrop =
  | "NONE"
  | "BLACK"
  | "WHITE"
  | "GREY"
  | "BRAND"
  | "BLUR"
  | "TRANSPARENT";

export const STUDIO_BACKDROPS: StudioBackdrop[] = [
  "NONE",
  "BLACK",
  "WHITE",
  "GREY",
  "BRAND",
  "BLUR",
  "TRANSPARENT",
];

export const STUDIO_BACKDROP_LABELS: Record<StudioBackdrop, string> = {
  NONE: "Original",
  BLACK: "Black",
  WHITE: "White",
  GREY: "Studio grey",
  BRAND: "Brand color",
  BLUR: "Blur",
  TRANSPARENT: "Transparent",
};

export type StudioMaskSource = "DEVICE" | "SERVER";
export type StudioRefineStatus =
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED"
  | "SKIPPED";
export type StudioRenderSize = "FULL" | "THUMB";

export interface PhotoStudioSettings {
  defaultBackdrop: StudioBackdrop;
  brandBackdropColor: string | null;
  effectiveBrandColor: string;
  autoRefine: boolean;
  exportShowLogo: boolean;
  exportWatermarkText: string | null;
  exportDisclaimerText: string;
  cutoutServiceAvailable: boolean;
}

export type PhotoStudioSettingsUpdate = Partial<
  Pick<
    PhotoStudioSettings,
    | "defaultBackdrop"
    | "brandBackdropColor"
    | "autoRefine"
    | "exportShowLogo"
    | "exportWatermarkText"
    | "exportDisclaimerText"
  >
>;

export interface AttachmentStudioSummary {
  backdrop: StudioBackdrop | null;
  effectiveBackdrop: StudioBackdrop;
  hasMask: boolean;
  maskSource: StudioMaskSource | null;
  refineStatus: StudioRefineStatus | null;
  hasOriginal: boolean;
}

export interface AttachmentStudioState extends AttachmentStudioSummary {
  attachmentId: string;
  gcsPath: string;
  isImage: boolean;
  maskUpdatedAt: string | null;
}

export interface StudioRender {
  attachmentId: string;
  backdrop: StudioBackdrop;
  size: StudioRenderSize;
  signedUrl: string | null;
  width: number | null;
  height: number | null;
  maskSource: StudioMaskSource | null;
  // Batch thumbnails only: not made in this request yet; ask again.
  pending?: boolean;
}

export interface StudioRenderBatchRequest {
  attachmentIds: string[];
  size?: StudioRenderSize;
  backdrop?: StudioBackdrop | null;
}

export interface PairAlignment {
  scale: number;
  rotationDeg: number;
  dx: number;
  dy: number;
}

export type PhotoStudioJobScope =
  | "VISIT"
  | "PATIENT"
  | "DATE_RANGE"
  | "ATTACHMENTS";
export type PhotoStudioJobStatus =
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";

export interface PhotoStudioJobCreate {
  scope: PhotoStudioJobScope;
  noteId?: string | null;
  patientId?: string | null;
  startDate?: string | null; // yyyy-MM-dd, clinic-local day
  endDate?: string | null;
  attachmentIds?: string[] | null;
  // The gallery's other filters, so the job picks the photos the gallery shows.
  tagIds?: string[] | null;
  optedInToSocialMedia?: boolean | null;
  backdrop?: StudioBackdrop | null;
  refine?: boolean;
}

export interface PhotoStudioJob {
  id: string;
  status: PhotoStudioJobStatus;
  scope: PhotoStudioJobScope;
  noteId: string | null;
  patientId: string | null;
  startDate: string | null;
  endDate: string | null;
  backdrop: StudioBackdrop | null;
  refine: boolean;
  totalCount: number;
  processedCount: number;
  failedCount: number;
  skippedCount: number;
  errorMessage: string | null;
  createdDate: string;
  startedAt: string | null;
  completedAt: string | null;
  createdById: string | null;
}

export type MarketingExportSize = "SQUARE" | "PORTRAIT" | "STORY";
export type MarketingExportLayout = "SIDE_BY_SIDE" | "STACKED";

export const MARKETING_EXPORT_DIMENSIONS: Record<
  MarketingExportSize,
  { width: number; height: number }
> = {
  SQUARE: { width: 1080, height: 1080 },
  PORTRAIT: { width: 1080, height: 1350 },
  STORY: { width: 1080, height: 1920 },
};

export interface MarketingPhotoConsent {
  patientId: string;
  consented: boolean | null;
  recordedAt: string | null;
  recordedByName: string | null;
}

export interface MarketingExportCreate {
  patientId: string;
  beforeAttachmentId: string;
  afterAttachmentId: string;
  backdrop: StudioBackdrop;
  size: MarketingExportSize;
  layout: MarketingExportLayout;
  alignment?: PairAlignment | null;
  labels?: { before: string; after: string };
  showLogo?: boolean | null;
  watermarkText?: string | null;
  disclaimerText?: string | null;
}

export interface MarketingExport {
  id: string;
  patientId: string;
  beforeAttachmentId: string | null;
  afterAttachmentId: string | null;
  size: MarketingExportSize;
  layout: MarketingExportLayout;
  backdrop: StudioBackdrop;
  signedUrl: string;
  width: number;
  height: number;
  createdDate: string;
  createdById: string | null;
}

export const MARKETING_CONSENT_REQUIRED = "MARKETING_CONSENT_REQUIRED";
